/**
 * EvalSummaryCard
 *
 * Org_executive / executive dashboard card matching Figma 3639:223416.
 * Shows the latest COMPLETED overall evaluation level for the user's
 * department and deep-links into EvaluationDashboard, scoped to that same
 * period, on tap.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useNavigation, CommonActions } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';

import { COLORS, FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { evaluationDashboardApi } from '../services/evaluationApi';
import type { DashboardEntity } from '../types/evaluation';

import { tokens } from '@design';
import { getTextAlign } from '../utils/rtl';
type NavigationProp = NativeStackNavigationProp<any>;

interface EvalSummaryCardProps {
  /** SSO department id of the org_executive's organization */
  departmentId: number;
  /** Optional year filter — defaults to the current year */
  year?: number;
}

interface LevelInfo {
  label: string;
  color: string;
}

const PERIOD_ORDER: Record<string, number> = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };

const getLevel = (score: number | null): LevelInfo => {
  if (score === null || score === undefined) {
    return { label: 'لا يوجد تقييم', color: COLORS.neutral400 };
  }
  if (score >= 90) return { label: 'مستوى التميّز', color: '#DC9333' };
  if (score >= 70) return { label: 'مستوى متقدم', color: '#6BB36F' };
  if (score >= 50) return { label: 'مستوى متوسط', color: '#E26B3A' };
  return { label: 'مستوى أساسي', color: '#844AAB' };
};

const StarIcon = ({ size = 40, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const EyeIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5" />
  </Svg>
);

export default function EvalSummaryCard({ departmentId, year }: EvalSummaryCardProps) {
  const navigation = useNavigation<NavigationProp>();
  const { colors } = useTheme();

  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState<DashboardEntity | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params: Record<string, string | number> = {
          // Quarterly only. PERIOD_ORDER below knows Q1–Q4; a monthly row
          // would score 0 and sort unpredictably against them.
          frequency: 'quarterly',
        };
        if (year) params.year = year;
        const data = await evaluationDashboardApi.list(params);
        if (cancelled) return;
        const mine = data.filter((e) => e.department_id === departmentId);
        const sorted = [...mine].sort((a, b) => {
          const yearDelta = (b.year ?? 0) - (a.year ?? 0);
          if (yearDelta !== 0) return yearDelta;
          return (PERIOD_ORDER[b.period ?? ''] ?? 0) - (PERIOD_ORDER[a.period ?? ''] ?? 0);
        });
        // Take the most recent COMPLETED evaluation, not the most recent
        // CYCLE. Opening a new quarter creates a not_started row for every
        // department immediately, so `sorted[0]` was the freshly-opened
        // quarter — score null — and the card read "لا يوجد تقييم" while an
        // approved, scored result sat one or two quarters back. Falls through
        // to null (the empty state) when nothing has been approved yet.
        const completed = sorted.find(
          (e) => e.status === 'approved'
            && e.overall_score !== null
            && e.overall_score !== undefined,
        );
        setLatest(completed ?? null);
      } catch {
        if (!cancelled) setLatest(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departmentId, year]);

  const score = latest?.overall_score !== null && latest?.overall_score !== undefined
    ? Number(latest.overall_score)
    : null;
  const level = getLevel(score);
  const hasEvaluation = latest !== null && score !== null;

  const handleViewDetails = () => {
    // Carry the period the card is REPORTING on, so the dashboard opens on
    // the same evaluation the user just read a level off. Without this it
    // fell back to the calendar's previous quarter — typically an unscored
    // one — and the numbers appeared to vanish on tap.
    const params: Record<string, string> = { frequency: 'quarterly' };
    if (latest?.period && latest?.year) {
      params.period = latest.period;
      params.year = String(latest.year);
    }
    navigation.dispatch(
      CommonActions.navigate({
        name: 'EvaluationStack',
        params: { screen: 'EvaluationDashboard', params },
      }),
    );
  };

  return (
    <TouchableOpacity
      style={[
        styles.card,
        { backgroundColor: colors.card, borderColor: colors.cardBorder },
      ]}
      onPress={handleViewDetails}
      activeOpacity={0.7}
    >
      <View style={styles.headerRow}>
        <View style={styles.titleColumn}>
          <Text style={[styles.title, { color: colors.textPrimary }]} numberOfLines={2}>
            {'التقييم\nالعام'}
          </Text>
        </View>
        <View style={styles.iconWrap}>
          <StarIcon size={40} color={colors.textPrimary} />
        </View>
      </View>

      <View style={styles.body}>
        {loading ? (
          <ActivityIndicator size="small" color={COLORS.primary} />
        ) : (
          <>
            <Text style={[styles.levelLabel, { color: level.color }]} numberOfLines={1}>
              {level.label}
            </Text>
            <View style={styles.detailsLinkRow}>
              <Text style={[styles.detailsLinkText, { color: colors.textPrimary }]}>
                عرض التفاصيل
              </Text>
              <EyeIcon size={16} color={colors.textPrimary} />
            </View>
          </>
        )}
      </View>
    </TouchableOpacity>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  card: {
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
    minHeight: 132,
    // Figma drop-shadow: 0 1px 3px rgba(0,0,0,0.1)
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  headerRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  titleColumn: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    lineHeight: 20,
    textAlign: getTextAlign(isRTL),
  },
  iconWrap: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  levelLabel: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.bold,
    textAlign: 'center',
  },
  detailsLinkRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: 40,
  },
  detailsLinkText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
  },
});

const styles = getStyles(true);
